import React, { useState, useRef, useEffect } from 'react';
import Button from '../buttons/Button';
import { faMicrophone, faPauseCircle } from '@fortawesome/free-solid-svg-icons';

interface OfflineAudioRecorderProps {
  /** now supplies both transcript *and* audio blob */
  onSubmit: (transcript: string, audio: Blob) => void;
}

const OfflineAudioRecorder: React.FC<OfflineAudioRecorderProps> = ({
  onSubmit,
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const transcriptRef = useRef<string>('');
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const initializeRecognition = () => {
    const SpeechRecognitionClass =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognitionClass) {
      alert('Speech Recognition is not supported in this browser.');
      return null;
    }

    const recognition = new SpeechRecognitionClass();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          transcriptRef.current += result[0].transcript + ' ';
        }
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error', event.error);
    };

    // When recognition actually ends, THEN stop the recorder
    recognition.onend = () => {
      console.debug('Speech recognition ended—stopping recorder now.');
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
    };

    return recognition;
  };

  const handleStart = async () => {
    // Tear down any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }

    const recognition = initializeRecognition();
    if (!recognition) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: 'audio/webm',
        });
        onSubmit(transcriptRef.current.trim(), audioBlob);

        // cleanup
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        mediaRecorderRef.current = null;
      };

      // reset transcript & start both
      transcriptRef.current = '';
      recognition.start();
      recognitionRef.current = recognition;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error('Could not start audio recording:', err);
      alert('Microphone access denied.');
    }
  };

  const handleStop = () => {
    // Only stop the speech recognizer; its onend will stop the recorder
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.abort();
      recognitionRef.current = null;
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== 'inactive'
      ) {
        mediaRecorderRef.current.stop();
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div style={{ textAlign: 'center' }}>
      <Button
        type={isRecording ? 'default' : 'go'}
        onClick={isRecording ? handleStop : handleStart}
        text={isRecording ? 'Stop' : 'Start'}
        fontAwesomeIcon={isRecording ? faPauseCircle : faMicrophone}
        label={isRecording ? 'Stop' : 'Start'}
      />
    </div>
  );
};

export default OfflineAudioRecorder;
